/**
 * The one owner for each shared fact (rd round 12, lane A4).
 *
 * WHY THIS EXISTS (rc-4 "facts"). "How many cards wait", "is a bank
 * connected", "what blocks a month", "who is reading" and "what state is this
 * checklist step in" were each computed on several pages, from different
 * reads, with different defaults. Two pages could state one fact differently
 * and both be "right" against their own read. Round 12 fixes the cause once:
 * the fact is decided HERE, and wave B moves the pages onto these functions.
 *
 * WHAT LIVES HERE
 *   - `readerRole`: owner decision 10, unknown role = the account manager.
 *   - `isWithOwner` / `reviewCards`: the one card-count derivation.
 *   - `monthAction`: owner decision 11, what a month is waiting on and who
 *     acts, with the pages that act on it.
 *   - `bankConnection` / `cashFigure`: is a bank connected, and what is cash.
 *   - `checklistView` / `actionableFor`: the one checklist reading.
 *
 * WHAT IT CANNOT DO. It cannot move a read or change a request: fetching stays
 * on the pages (moving reads changes the pinned request budget), so this
 * module decides over data the caller already holds. It never changes the
 * Worker's own `status`; it says what that status means to this reader.
 *
 * The facts guard (`rd-r12-a4-facts-guard.test.ts`) fails a page that derives
 * one of these facts itself.
 */

import type { AdminMonth, BalancesSnapshot, MonthBlockerKey } from "../console/types.js";
import {
  isQueueItemEscalated,
  type CompanyHealth,
  type CompanySlug,
  type OnboardingStatus,
  type OnboardingStepKey,
  type OnboardingStepState,
  type QueueItem,
  type RouteName,
  type StaffRole,
} from "../types.js";
import type { MeasuredSet } from "./measured-claim.js";
import { CASH_ACCOUNT_TYPES, sumCashUsdCents, type UsdCents } from "./money.js";
import { currentRole } from "./nav-bus.js";
import { WHO_WORDS, checklistStep } from "./face.js";
import { pluralText } from "./display-words.js";
import { sentence } from "./sentences.js";

// ---------------------------------------------------------------------------
// Who is reading (owner decision 10)
// ---------------------------------------------------------------------------

/**
 * The person reading the screen.
 *
 * Owner decision 10 (2026-10-31, orchestrator's recommendation accepted with
 * "Your recs are good."): "When the reader's role is unknown, assume the
 * account manager (least access)."
 *
 * NOTE FOR WAVE B (do not lose this): five pages read the role before the
 * shell has registered it on the nav bus (`pages/waiting.ts:86`,
 * `pages/statements.ts:326`, `pages/transactions.ts:1540`,
 * `pages/check-now.ts:200`, `pages/receipt-shelf.ts:111` all default to the
 * owner today). Calling `readerRole()` with no argument during that window
 * returns `"account_manager"` and would demote a real owner for one render.
 * Each wave-B lane passes the role its page already knows (`readerRole(role)`)
 * or reads it after registration; an owner must never be demoted by the
 * default.
 */
export function readerRole(explicit?: StaffRole | null): StaffRole {
  if (explicit === "owner" || explicit === "account_manager") return explicit;
  const live = currentRole();
  return live === "owner" ? "owner" : "account_manager";
}

// ---------------------------------------------------------------------------
// Review cards (rc-4 C1, C2, C3, C7, C8)
// ---------------------------------------------------------------------------

/**
 * One card is with the owner: it was escalated and has not been returned.
 *
 * This is THE per-card fact. Wave B replaces every per-card
 * `isQueueItemEscalated(item)` call with it, so a page never re-decides what
 * "with the owner" means. A3 publishes the Worker's TypeScript twins in
 * `ingest-worker/src/queue_scope.ts`; today this delegates to the portal's one
 * predicate in `types.ts`, which mirrors the Worker's rule
 * (`escalatedAt` set and not cleared).
 */
export function isWithOwner(item: QueueItem): boolean {
  return isQueueItemEscalated(item);
}

export interface ReviewCards {
  /** The cards this reader can answer now. */
  openable: QueueItem[];
  /** The cards the owner holds. */
  withOwner: QueueItem[];
  /** The caller's honest coverage flag, carried through. */
  complete: boolean;
}

/**
 * How many cards wait, and whose they are, from ONE set.
 *
 * The Worker's rule: only pending cards wait; an escalated card is with the
 * owner and is not an account manager's to answer (the owner still sees it, so
 * resolution stays on the existing path). A count built from a capped or
 * failed read must say so: `complete` is the caller's measured-completeness
 * flag and the page prints `countText(…, complete)` from it.
 */
export function reviewCards(
  set: MeasuredSet<QueueItem>,
  role: StaffRole,
  company?: CompanySlug,
): ReviewCards {
  const scoped =
    company === undefined
      ? set.items
      : set.items.filter((item) => item.company === company);
  const pending = scoped.filter((item) => item.status === "pending");
  const withOwner = pending.filter((item) => isWithOwner(item));
  const openable =
    role === "account_manager"
      ? pending.filter((item) => !isWithOwner(item))
      : [...pending];
  return { openable, withOwner, complete: set.measureComplete };
}

// ---------------------------------------------------------------------------
// What a month is waiting on (owner decision 11)
// ---------------------------------------------------------------------------

/** Who acts on a month blocker, from this reader's view. */
export type MonthActor = "you" | "owner" | "hub" | "system";

/** The shortest true word for the month, from `rd-polish-rules.md`. */
export type MonthWord =
  | "Needs you"
  | "Waiting for the owner"
  | "With the hub"
  | "Waiting"
  | "Closed"
  | "Needs closing again"
  | "Nothing waiting";

export interface MonthAction {
  word: MonthWord;
  /** One plain sentence naming the first blocker this reader can act on. */
  sentence: string;
  actor: MonthActor;
  /** The page that acts on it, or null when no page can. */
  route: RouteName | null;
}

interface BlockerRule {
  actor: (role: StaffRole) => MonthActor;
  route: RouteName | null;
  sentence: (count: number, role: StaffRole) => string;
}

/** The page a surface names after a route, for the sentence's tail. */
const ROUTE_PAGE_WORDS: Partial<Record<RouteName, string>> = {
  queue: "To review",
  waiting: "Waiting",
  statements: "Statement checks",
  errors: "Problems",
};

/**
 * Every `MonthBlockerKey` (`contracts/books-api.yaml`), mapped for both roles.
 * Typed as a total record (round-2 LOW 7): adding a key to the contract is a
 * compile error here, not only a test failure. It never changes the Worker's
 * own `status`.
 */
const BLOCKER_RULES: Readonly<Record<MonthBlockerKey, BlockerRule>> = {
  close_blocked: {
    actor: () => "you",
    route: "errors",
    sentence: () => "The month close was stopped.",
  },
  reclose_needed: {
    actor: () => "system",
    route: null,
    sentence: () =>
      "The month-end close closes this month again. You cannot close it from here.",
  },
  closed_month_write_blocked: {
    actor: () => "you",
    route: "errors",
    sentence: (count) =>
      `${pluralText(count, "booking", "bookings")} tried to change this closed month.`,
  },
  cards_to_review: {
    actor: () => "you",
    route: "queue",
    sentence: (count) => `${pluralText(count, "card", "cards")} to review.`,
  },
  // Owner decision 11: a card the owner holds is not the account manager's to
  // answer, so it reads "Waiting for the owner" rather than "Needs you".
  cards_with_owner: {
    actor: (role) => (role === "owner" ? "you" : "owner"),
    route: "queue",
    sentence: (count, role) =>
      role === "owner"
        ? `${pluralText(count, "escalated card", "escalated cards")} need${count === 1 ? "s" : ""} you.`
        : `${pluralText(count, "card", "cards")} ${count === 1 ? "is" : "are"} with the owner.`,
  },
  cards_with_practice: {
    actor: () => "hub",
    route: "waiting",
    sentence: (count) =>
      `${pluralText(count, "card", "cards")} ${count === 1 ? "is" : "are"} with the hub.`,
  },
  // Confirming a statement is owner-only (`ingest-worker/src/admin.ts`), so the
  // account manager reads "Waiting for the owner" for BOTH statement keys
  // (owner decision 11).
  statements_not_matching: {
    actor: (role) => (role === "owner" ? "you" : "owner"),
    route: "statements",
    sentence: (count) =>
      `${pluralText(count, "bank statement", "bank statements")} ${
        count === 1 ? "does" : "do"
      } not match the books.`,
  },
  statements_to_confirm: {
    actor: (role) => (role === "owner" ? "you" : "owner"),
    route: "statements",
    sentence: (count) =>
      `${pluralText(count, "bank statement", "bank statements")} ${
        count === 1 ? "needs" : "need"
      } checking.`,
  },
  statements_saving: {
    actor: () => "system",
    route: null,
    sentence: (count) =>
      `${pluralText(count, "statement check", "statement checks")} will be saved on the next booking run.`,
  },
  check_failed_after_close: {
    actor: () => "you",
    route: "errors",
    sentence: () => "A balance check failed after closing.",
  },
  bank_lines_waiting: {
    actor: () => "system",
    route: null,
    sentence: (count) =>
      `${pluralText(count, "bank line", "bank lines")} will be booked on the next run.`,
  },
  bank_lines_failed: {
    actor: () => "you",
    route: "errors",
    sentence: (count) =>
      `${pluralText(count, "bank line", "bank lines")} could not be booked.`,
  },
};

/** The keys the table covers, for the exhaustiveness test. */
export const MONTH_BLOCKER_RULE_KEYS: readonly string[] = Object.keys(BLOCKER_RULES);

function knownRule(key: string): BlockerRule | undefined {
  return Object.prototype.hasOwnProperty.call(BLOCKER_RULES, key)
    ? BLOCKER_RULES[key as MonthBlockerKey]
    : undefined;
}

/** Priority: what the reader can do, then whose it is, then the system. */
const ACTOR_ORDER: readonly MonthActor[] = ["you", "owner", "hub", "system"];

/**
 * The word for every actor except the owner. The owner's word has ONE home,
 * `ui/sentences.ts` (`waiting_for_owner`), and is read through `actorWord` so
 * the two files cannot drift (carryover E-B9).
 */
const ACTOR_WORD: Readonly<Record<Exclude<MonthActor, "owner">, MonthWord>> = {
  you: "Needs you",
  hub: "With the hub",
  system: "Waiting",
};

function actorWord(actor: MonthActor, role: StaffRole): MonthWord {
  if (actor === "owner") return sentence("waiting_for_owner", role) as MonthWord;
  return ACTOR_WORD[actor];
}

/** What a status says when no blocker names the cause. */
function statusOnlyAction(month: AdminMonth): MonthAction {
  switch (month.status) {
    case "closed":
      return { word: "Closed", sentence: "Closed.", actor: "system", route: null };
    case "closed_check_failed":
      return {
        word: "Needs you",
        sentence: "A balance check failed after this month closed. Open Problems to see why.",
        actor: "you",
        route: "errors",
      };
    case "reopened":
      return {
        word: "Needs closing again",
        sentence: "This month was reopened and needs closing again.",
        actor: "system",
        route: null,
      };
    case "needs_you":
      return {
        word: "Needs you",
        sentence: "A card needs you. Open To review.",
        actor: "you",
        route: "queue",
      };
    case "waiting":
      return {
        word: "Waiting",
        sentence: "Waiting for the next booking run.",
        actor: "system",
        route: null,
      };
    case "open":
      return {
        word: "Nothing waiting",
        sentence: "Nothing is blocking this month.",
        actor: "system",
        route: null,
      };
    default:
      return {
        word: "Waiting",
        sentence: "This month's state is not known.",
        actor: "system",
        route: null,
      };
  }
}

/**
 * What one month is waiting on for this reader.
 *
 * Owner decision 11 (2026-10-31, accepted with "Your recs are good."): "On the
 * AM's Months page, statements that only the owner can confirm read "Waiting
 * for the owner", not "Needs you"."
 *
 * The sentence names the FIRST blocker this reader can act on (then the first
 * owned by someone else, then the first system wait) and the page that acts on
 * it. A month with no known blocker falls back to its status, in the same
 * words the Months page already uses. The Worker's `status` is never changed.
 */
export function monthAction(month: AdminMonth, role: StaffRole): MonthAction {
  const known = month.blockers
    .map((blocker) => ({ blocker, rule: knownRule(blocker.key) }))
    .filter(
      (row): row is { blocker: AdminMonth["blockers"][number]; rule: BlockerRule } =>
        row.rule !== undefined,
    );
  if (known.length === 0) return statusOnlyAction(month);

  for (const actor of ACTOR_ORDER) {
    const match = known.find((row) => row.rule.actor(role) === actor);
    if (match === undefined) continue;
    // A route is offered only to the reader who acts on it: an account manager
    // never gets a page for something only the owner can do.
    const route = actor === "owner" ? null : match.rule.route;
    const body = match.rule.sentence(match.blocker.count, role);
    const page = route === null ? "" : ROUTE_PAGE_WORDS[route];
    // The sentence names the page that acts on it, so the reader is never left
    // with "2 cards to review" and no way there.
    const sentence = /[.!]$/u.test(body) ? body : `${body}.`;
    return {
      word: actorWord(actor, role),
      sentence: page === undefined || page === "" ? sentence : `${sentence} Open ${page}.`,
      actor,
      route,
    };
  }
  return statusOnlyAction(month);
}

// ---------------------------------------------------------------------------
// Is a bank connected, and what is cash (rc-4 B1-B8, round-11 item 5)
// ---------------------------------------------------------------------------

export type BankConnection = "none" | "healthy" | "needs_reconnect" | "never_read";

/**
 * Does a connected bank need a person to sign in again?
 *
 * This answers the STATUS question only (an item the bank refused), so a bank
 * that has never produced a reading but is marked `login_required` still needs
 * a reconnect. `bankConnection` answers the wider "is it connected and read"
 * question, where such an item is `never_read` (its own test pins that). Both
 * live here so no page reads `plaidItems` itself.
 */
export function bankNeedsReconnect(
  health: CompanyHealth | null | undefined,
): boolean {
  if (health === null || health === undefined) return false;
  return health.plaidItems.some((item) => item.status !== "healthy");
}

/**
 * When a connected bank was last checked, from the item's own reading.
 *
 * This is the ONE place a page may read `plaidItems` for a time: the facts
 * guard subtracts this module, so Home and Connection status share the fact
 * instead of each reading the list (B2a carryover: `bankConnection` owns "is a
 * bank connected", this owns "when was it checked"). Null when no connected
 * item has ever produced a reading.
 */
export function bankCheckedAt(health: CompanyHealth | null | undefined): string | null {
  if (health === null || health === undefined) return null;
  let latest: string | null = null;
  for (const item of health.plaidItems) {
    const at = item.lastSyncedAt;
    if (at === undefined || at === null || at === "") continue;
    if (latest === null || at > latest) latest = at;
  }
  return latest;
}

/**
 * Is a bank connected, and is it working?
 *
 * `"none"` is a company with NO Plaid item, whatever its last-run fields say:
 * a company that was never linked can still carry an old successful run, and
 * the Health page's "Last bank update finished" then described a bank that
 * does not exist (round 12 fixes that page with this function).
 *
 * `"never_read"` is "we have no reading at all": the health read is missing,
 * or every connected item has never produced one (`lastSyncedAt` absent or
 * null). It is never a claim that no bank is connected (round-2 MEDIUM 4: a
 * connected item that had never synced used to read "healthy").
 */
export function bankConnection(
  health: CompanyHealth | null | undefined,
): BankConnection {
  if (health === null || health === undefined) return "never_read";
  if (health.plaidItems.length === 0) return "none";
  const everSynced = health.plaidItems.some(
    (item) => item.lastSyncedAt !== null && item.lastSyncedAt !== undefined && item.lastSyncedAt !== "",
  );
  if (!everSynced) return "never_read";
  const broken = health.plaidItems.some((item) => item.status !== "healthy");
  return broken ? "needs_reconnect" : "healthy";
}

export type CashFigure =
  | { kind: "cash"; cents: UsdCents }
  | { kind: "no_cash_account" }
  | { kind: "no_accounts" }
  | { kind: "unknown" };

/**
 * What the bank tile may say about cash.
 *
 * The defect this closes: `sumCashUsdCents` returns 0n when accounts exist but
 * none is a depository account, so Home read "Bank $0.00" for a company whose
 * connected bank has only a credit card (round-11 item 5). The four kinds are
 * different facts and the page words each one; `no_cash_account` and
 * `unknown` are never printed as "$0.00".
 *
 * Round-2 MEDIUM 5: pass the `bankConnection` result as well and the two facts
 * cannot disagree. `"none"` means the snapshot is stale (no Plaid item), so it
 * reads `no_accounts`; `"never_read"` means we have no reading, so it reads
 * `unknown`. The connection is optional because a wave-B page may not hold the
 * health read; the snapshot alone is then all this function knows.
 *
 * The "is it cash" decision is `CASH_ACCOUNT_TYPES` in `ui/money.ts`, not a
 * second literal here.
 *
 * MUST STAY TRUE: a connected depository account with a real $0.00 balance
 * still reads "$0.00" — it is `{ kind: "cash", cents: 0n }`.
 */
export function cashFigure(
  snapshot: BalancesSnapshot | null | undefined,
  bank?: BankConnection,
): CashFigure {
  if (bank === "none") return { kind: "no_accounts" };
  if (bank === "never_read") return { kind: "unknown" };
  if (snapshot === null || snapshot === undefined) return { kind: "unknown" };
  if (snapshot.accounts.length === 0) return { kind: "no_accounts" };
  const hasCash = snapshot.accounts.some((account) => CASH_ACCOUNT_TYPES.has(account.type));
  if (!hasCash) return { kind: "no_cash_account" };
  const cents = sumCashUsdCents(snapshot.accounts);
  return cents === null ? { kind: "unknown" } : { kind: "cash", cents };
}

// ---------------------------------------------------------------------------
// The onboarding checklist (rc-4 A1-A4)
// ---------------------------------------------------------------------------

/** Who a step is waiting on, from this reader's view. */
export type ChecklistWaiter = "you" | "owner" | "system" | "hub" | null;

export interface ChecklistStepView {
  key: OnboardingStepKey;
  state: OnboardingStepState;
  waiter: ChecklistWaiter;
  /** What this reader can do here, or null when there is no control. */
  action: string | null;
}

export interface ChecklistView {
  steps: ChecklistStepView[];
  /** The line the header shows: the next step, or what is still open. */
  nextLine: string;
  /** Required steps still open before go-live. */
  openRequired: number;
}

/** The only rows a person may tick by hand (the status contract's hand steps). */
const HAND_STEP_KEYS: ReadonlySet<OnboardingStepKey> = new Set<OnboardingStepKey>([
  "hub_side_mapping",
  "tenant_portal",
]);

/**
 * Required steps no console control can complete: they are switched on outside
 * the console (rd r13 L3.4).
 *
 * `statements_option` is the statements-download option, which today lives in
 * a code constant (`STATEMENTS_DOWNLOAD_COMPANIES`, empty until OB-9) rather
 * than in a stored setting. The catalogue calls its owner the owner, so Home
 * counted it as "waiting on you" and the checklist asked the owner for an
 * approval with no button anywhere. It is not this reader's work, so it is
 * not counted as such; the step itself says who does it and that it is
 * switched on outside the console.
 */
export const OUTSIDE_CONSOLE_STEP_KEYS: ReadonlySet<OnboardingStepKey> =
  new Set<OnboardingStepKey>(["statements_option"]);

/** The control words for a step this reader can act on. */
function actionFor(key: OnboardingStepKey, waiter: ChecklistWaiter): string | null {
  if (waiter !== "you") return null;
  if (HAND_STEP_KEYS.has(key)) return "Mark it done";
  if (key === "api_key") return "Issue the API key";
  if (key === "link_invitation") return "Issue the bank invitation";
  return null;
}

/** Who the reader is, in the contract's `who` vocabulary. */
function readerWho(role: StaffRole): "owner" | "am" {
  return role === "owner" ? "owner" : "am";
}

/**
 * The checklist as this reader sees it.
 *
 * The STATE is checked before the role: a Done step never says "Waiting for
 * the owner." (rc-4 A1, HIGH — the AM read it on Done steps because the page
 * asked the role first). A step that is not done is waiting on whoever the
 * contract names: the reader ("you"), the owner, the system, or the hub/
 * tenant portal. A bank-link step waits on a person outside the console and
 * carries no control.
 *
 * `nextLine` says what the header should read: the next step and whose it is,
 * or — when the Worker sends no next step but required rows are open — what is
 * still open, never "Everything required is done." (rc-4 A2, HIGH: the Worker
 * sets `next` only on an actionable step, so a page that keyed off `next`
 * alone could contradict its own open-rows line).
 */
export function checklistView(status: OnboardingStatus, role: StaffRole): ChecklistView {
  const steps: ChecklistStepView[] = status.steps.map((step) => {
    let waiter: ChecklistWaiter = null;
    if (step.state !== "done") {
      if (step.who === "owner") waiter = role === "owner" ? "you" : "owner";
      else if (step.who === "am") waiter = role === "account_manager" ? "you" : null;
      else if (step.who === "system") waiter = "system";
      else if (step.who === "hub_team" || step.who === "portal_team") waiter = "hub";
    }
    return { key: step.key, state: step.state, waiter, action: actionFor(step.key, waiter) };
  });

  const openRequired = status.steps.filter(
    (step) => step.required && step.key !== "go_live" && step.state !== "done",
  ).length;

  return { steps, nextLine: nextLineFor(status, role, openRequired), openRequired };
}

function nextLineFor(
  status: OnboardingStatus,
  role: StaffRole,
  openRequired: number,
): string {
  const next = status.next;
  if (next !== null) {
    // Round-2 MEDIUM 3: the label is the portal's one step label. The Worker's
    // `title` is internal text (A4-4), and a raw key is not words.
    const label = checklistStep(next.key);
    const mine = next.who === readerWho(role);
    if (mine) return `Next: ${label}.`;
    const who = WHO_WORDS[next.who] ?? "someone else";
    return `Next: ${label} is waiting for ${who}.`;
  }
  if (openRequired > 0) {
    const open = status.steps
      .filter((step) => step.required && step.key !== "go_live" && step.state !== "done")
      .map((step) => checklistStep(step.key));
    return `Still open: ${open.join(", ")}.`;
  }
  return "Everything required is done.";
}

/**
 * The one count Today shows for "onboarding waiting on you": steps that are
 * waiting, whose owner is this reader, and that this reader can actually
 * complete in the console. It replaces Home's local derivation
 * (`home.ts:2097-2098`) so Home and the checklist cannot disagree. A step
 * switched on outside the console is somebody else's work, so it is not
 * counted here (rd r13 L3.4).
 */
export function actionableFor(status: OnboardingStatus, role: StaffRole): number {
  const who = readerWho(role);
  return status.steps.filter(
    (step) =>
      step.state === "waiting" &&
      step.who === who &&
      !OUTSIDE_CONSOLE_STEP_KEYS.has(step.key),
  ).length;
}
